import { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Layout from '@/components/Layout';
import { useAuth } from '@/contexts/AuthContext';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { generateRekapPDF } from '@/utils/pdfGenerator';
import { resolveImgUrl } from '@/utils/imgUrl';
import { formatHalaman, describeSetoran } from '@/utils/halaman';
import { TrendingUp, BookOpen, CheckCircle, FileText, BarChart2, BookCheck, Repeat, Clock, Newspaper, ShoppingBag, Heart, Moon } from 'lucide-react';
import NotificationBell from '@/components/NotificationBell';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Urutan hafalan BQ: 30, 29, 27, lalu 1 s/d 26, 28
const BQ_ORDER = [30, 29, 27, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12,
                  13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25, 26, 28];

const KELAS_CONFIG = {
  D: { label: "I'dad",    color: 'bg-amber-400/20 text-amber-100',   ring: 'ring-amber-400'   },
  C: { label: 'Uula',     color: 'bg-yellow-400/20 text-yellow-100', ring: 'ring-yellow-400'  },
  B: { label: 'Wustha',   color: 'bg-blue-400/20 text-blue-100',     ring: 'ring-blue-400'    },
  A: { label: 'Ulya',     color: 'bg-emerald-400/20 text-emerald-100', ring: 'ring-emerald-300' },
  M: { label: 'Ali/Mahir', color: 'bg-purple-400/20 text-purple-100', ring: 'ring-purple-400' },
};

// Target hafalan bulanan per kelas metodologi BQ
const TARGET_KELAS = {
  D: { halaman: 10, label: '10 Hal / Bulan'    }, // I'dad  — fokus Iqro/Binnazhar
  C: { halaman: 15, label: '0.75 Juz / Bulan'  }, // Uula   — mulai menghafal
  B: { halaman: 20, label: '1 Juz / Bulan'     }, // Wustha — standar
  A: { halaman: 25, label: '1.5 Juz / Bulan'   }, // Ulya   — mahir
  M: { halaman: 30, label: '2 Juz / Bulan'     }, // Ali/Mahir — mutqin
};

const JENIS_COLOR = {
  ziyadah: 'bg-emerald-100 text-emerald-700',
  murajaah: 'bg-blue-100 text-blue-700',
  bin_nadhar: 'bg-purple-100 text-purple-700',
  bin_nadzar: 'bg-purple-100 text-purple-700',
  tasmi: 'bg-amber-100 text-amber-700',
  iqro: 'bg-orange-100 text-orange-700',
};
const JENIS_LABEL = {
  ziyadah: 'Ziyadah', murajaah: 'Murajaah', bin_nadhar: 'Binnadzar',
  bin_nadzar: 'Binnadzar',
  tasmi: "Tasmi'", iqro: 'Iqro',
};

const HARI = ['Minggu','Senin','Selasa','Rabu','Kamis',"Jum'at",'Sabtu'];
const BULAN = ['Januari','Februari','Maret','April','Mei','Juni','Juli','Agustus','September','Oktober','November','Desember'];

function getGreeting(h) {
  if (h < 5)  return { sapa: 'Malam Barakah', pesan: 'Qiyamullail penuh keberkahan' };
  if (h < 12) return { sapa: 'Selamat Pagi', pesan: 'Semangat menambah hafalan hari ini!' };
  if (h < 15) return { sapa: 'Selamat Siang', pesan: 'Satu ayat per hari, satu langkah ke surga' };
  if (h < 18) return { sapa: 'Selamat Sore', pesan: 'Waktunya murojaah hafalan' };
  return { sapa: 'Selamat Malam', pesan: 'Review hafalan sebelum tidur' };
}

// ─── Circular Progress SVG ──────────────────────────────
function CircularProgress({ percent, size = 140 }) {
  const sw = 12;
  const r = (size - sw * 2) / 2;
  const circ = 2 * Math.PI * r;
  const offset = circ * (1 - Math.min(percent, 100) / 100);
  return (
    <div className="relative flex items-center justify-center" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="#d1fae5" strokeWidth={sw} />
        <motion.circle cx={size/2} cy={size/2} r={r} fill="none" stroke="#10b981" strokeWidth={sw}
          strokeDasharray={circ} strokeLinecap="round"
          initial={{ strokeDashoffset: circ }}
          animate={{ strokeDashoffset: offset }}
          transition={{ duration: 1.5, ease: 'easeOut' }}
        />
      </svg>
      <div className="absolute flex flex-col items-center justify-center">
        <span className="text-2xl font-bold text-emerald-800">{Math.min(percent, 100)}%</span>
        <span className="text-[10px] text-emerald-500 font-semibold uppercase tracking-wide">Bulan Ini</span>
      </div>
    </div>
  );
}

// ─── Stat Card ──────────────────────────────────────────
function StatCard({ icon: Icon, value, label, color, delay, testId }) {
  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay }}
      data-testid={testId}
      className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
      <div className={`w-9 h-9 rounded-xl bg-${color}-100 flex items-center justify-center mb-3`}>
        <Icon size={17} className={`text-${color}-600`} />
      </div>
      <p className={`text-2xl font-bold text-${color}-700`}>{value}</p>
      <p className="text-[11px] text-gray-400 font-medium mt-0.5">{label}</p>
    </motion.div>
  );
}

export default function SantriDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [setoran, setSetoran] = useState([]);
  const [progress, setProgress] = useState(null);
  const [santriData, setSantriData] = useState(null);
  const [settings, setSettings] = useState({});
  const [guruList, setGuruList] = useState([]);
  const [halaqahList, setHalaqahList] = useState([]);
  const [now] = useState(new Date());
  const [filterJenis, setFilterJenis] = useState('semua');

  const activeView = location.pathname.startsWith('/santri/tasmi') ? 'tasmi'
    : location.pathname.startsWith('/santri/progress') ? 'progress'
    : location.pathname.startsWith('/santri/setoran') ? 'setoran'
    : 'beranda';

  // ── Muat data utama
  const loadData = useCallback(async () => {
    if (!user?.santri_id) return;
    await Promise.all([
      axios.get(`${API}/setoran?santri_id=${user.santri_id}`).then(r => setSetoran(r.data)).catch(() => {}),
      axios.get(`${API}/santri/${user.santri_id}/progress`).then(r => setProgress(r.data)).catch(() => {}),
      axios.get(`${API}/santri/${user.santri_id}`).then(r => setSantriData(r.data)).catch(() => {}),
    ]);
  }, [user]);

  useEffect(() => {
    axios.get(`${API}/settings`).then(r => setSettings(r.data)).catch(() => {});
    axios.get(`${API}/guru`).then(r => setGuruList(r.data)).catch(() => {});
    axios.get(`${API}/halaqah`).then(r => setHalaqahList(r.data)).catch(() => {});
    loadData();
  }, [loadData]);

  // Auto-sinkronisasi setiap 30 detik agar data setoran baru dari Guru langsung terbaca
  useEffect(() => {
    if (!user?.santri_id) return;
    const timer = setInterval(loadData, 30000);
    return () => clearInterval(timer);
  }, [loadData]);

  // Derived data
  const thisMonth = `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}`;
  const setoranBulanIni = setoran.filter(s => s.tanggal?.startsWith(thisMonth));
  const halamanBulanIni = setoranBulanIni
    .filter(s => s.jenis === 'ziyadah')
    .reduce((sum, s) => sum + (s.jml_halaman || 0), 0);
  const kelasTarget  = santriData?.kelas ? TARGET_KELAS[santriData.kelas] : null;
  const targetBulanan = kelasTarget?.halaman ?? 20;
  const targetLabel   = kelasTarget ? kelasTarget.label : 'Target belum diset';

  const progressPct  = Math.round((halamanBulanIni / targetBulanan) * 100);

  const juzDone    = progress?.juz_completed   ?? 0;
  const currentJuz = progress?.current_juz     ?? null;
  const bqPct      = progress?.bq_progress_pct ?? 0;
  const totalHalaman = formatHalaman(progress?.total_pages || 0);
  const totalHalamanMurajaah = formatHalaman(progress?.total_pages_murajaah || 0);
  const totalHalamanBinnadzar = formatHalaman(
    setoran.filter(s => ['bin_nadzar', 'bin_nadhar'].includes(s.jenis))
           .reduce((sum, s) => sum + (s.jml_halaman || 0), 0)
  );

  const halaqahName = halaqahList.find(h => h.id === santriData?.halaqah_id)?.nama || '';

  const latestCatatan = setoran.find(s => s.catatan?.trim());
  const guruCatatan = guruList.find(g => g.user_id === latestCatatan?.guru_id);

  const greetData = getGreeting(now.getHours());

  const handleLogout = async () => { await logout(); navigate('/welcome'); };
  const handleCetakPDF = async () => {
    if (!santriData) return;
    try {
      toast.info('Menyiapkan rekap...');
      const [absensiRes, tasmiRes] = await Promise.all([
        axios.get(`${API}/absensi`).catch(() => ({ data: [] })),
        axios.get(`${API}/tasmi`).catch(() => ({ data: [] })),
      ]);
      await generateRekapPDF(santriData, setoran, absensiRes.data, tasmiRes.data, progress, settings);
      toast.success('Rekap Perkembangan berhasil diunduh');
    } catch { toast.error('Gagal membuat rekap'); }
  };

  return (
    <Layout>
      <div className="min-h-screen bg-gray-50">

        {/* ═══ BAGIAN 1: SMART HEADER ═══════════════════════════ */}
        <div className="bg-gradient-to-br from-emerald-950 via-emerald-800 to-emerald-700 px-5 pt-12 pb-6 relative overflow-hidden">
          {/* Decorative blobs */}
          <div className="absolute -top-12 -right-12 w-48 h-48 bg-white/5 rounded-full pointer-events-none" />
          <div className="absolute -bottom-10 -left-10 w-36 h-36 bg-amber-400/10 rounded-full pointer-events-none" />

          <div className="relative flex items-start gap-4">
            {/* Photo with gold ring */}
            <div className="flex-shrink-0">
              <div className="w-20 h-20 rounded-full ring-[3px] ring-amber-400 ring-offset-2 ring-offset-emerald-900 overflow-hidden bg-emerald-700 flex items-center justify-center shadow-xl">
                {santriData?.foto
                  ? <img src={resolveImgUrl(santriData.foto)} alt={user?.name} className="w-full h-full object-cover" />
                  : <span className="text-white font-bold text-2xl">{user?.name?.charAt(0)}</span>}
              </div>
            </div>

            {/* Info */}
            <div className="flex-1 min-w-0">
              <p className="text-emerald-300 text-[10px] uppercase tracking-widest mb-0.5">{greetData.sapa}</p>
              <h1 className="font-playfair text-xl font-bold text-white leading-tight truncate">{user?.name}</h1>
              {santriData?.kelas && (
                <span className={`inline-block mt-1 text-[11px] font-bold px-2.5 py-0.5 rounded-full ${KELAS_CONFIG[santriData.kelas]?.color}`}>
                  Kelas {santriData.kelas} · {KELAS_CONFIG[santriData.kelas]?.label}
                </span>
              )}
              {halaqahName && (
                <p className="text-emerald-300 text-[11px] mt-1 flex items-center gap-1">
                  <BookOpen size={10} /> {halaqahName}
                </p>
              )}
              <p className="text-emerald-400 text-[10px] mt-1.5 italic leading-snug">{greetData.pesan}</p>
            </div>

            {/* Notification bell */}
            <div className="flex-shrink-0 mt-1">
              <NotificationBell />
            </div>
          </div>

          {/* Date bar */}
          <div className="mt-4 bg-white/10 rounded-2xl px-4 py-2.5 flex items-center justify-between">
            <p className="text-emerald-200 text-xs">{HARI[now.getDay()]}, {now.getDate()} {BULAN[now.getMonth()]} {now.getFullYear()}</p>
            <button data-testid="santri-cetak-pdf-btn" onClick={handleCetakPDF}
              className="flex items-center gap-1.5 text-[11px] font-semibold text-amber-300 hover:text-amber-200">
              <FileText size={11} /> Rekap Perkembangan
            </button>
          </div>
        </div>

        {/* ═══ CONTENT VIEWS ════════════════════════════════════ */}
        <div className="px-4 py-5 pb-32 md:pb-8 space-y-5">

          {/* ── BERANDA VIEW ── */}
          {activeView === 'beranda' && (
            <>
              {/* ═══ PROGRES HAFALAN ═══ */}
              <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="font-semibold text-gray-800 text-sm">Progress Hafalan</h3>
                  </div>
                  <button onClick={() => navigate('/santri/progress')}
                    className="text-[11px] text-emerald-600 font-semibold">Lihat Detail →</button>
                </div>

                {/* Circular Progress */}
                <div className="flex items-center gap-5 mb-5">
                  <CircularProgress percent={progressPct} size={120} />
                  <div className="flex-1 space-y-3">
                    <div>
                      <div className="flex justify-between text-xs mb-1">
                        <span className="text-gray-500">Total Juz (Urutan BQ)</span>
                        <span className="font-bold text-blue-700">{bqPct}% — {juzDone}/30 Juz</span>
                      </div>
                      <div className="h-3 bg-blue-50 rounded-full overflow-hidden">
                        <motion.div className="h-full bg-blue-500 rounded-full"
                          initial={{ width: 0 }} animate={{ width: `${bqPct}%` }}
                          transition={{ duration: 1.5, ease: 'easeOut' }} />
                      </div>
                      {currentJuz && (
                        <p className="text-[10px] text-gray-400 mt-1">
                          Sedang menghafal: <span className="font-semibold text-blue-600">Juz {currentJuz}</span>
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Juz Grid — urutan BQ (29, 30, 1–28) */}
                <div className="grid grid-cols-10 gap-1">
                  {BQ_ORDER.map((juzNo, bqIdx) => {
                    const isDone    = bqIdx < juzDone;
                    const isCurrent = currentJuz !== null && bqIdx === juzDone;
                    return (
                      <div key={juzNo} data-testid={`juz-block-${juzNo}`}
                        className={`aspect-square rounded-lg flex items-center justify-center text-[9px] font-bold transition-all ${
                          isDone    ? 'bg-emerald-500 text-white shadow-sm'
                          : isCurrent ? 'bg-amber-400 text-white shadow-sm ring-1 ring-amber-500'
                          : 'bg-emerald-50 text-emerald-300'
                        }`}>{juzNo}</div>
                    );
                  })}
                </div>
              </div>

              {/* ═══ SETORAN TERAKHIR ═══ */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Setoran Terakhir</p>
                  <button onClick={() => navigate('/santri/setoran')}
                    className="text-[11px] text-emerald-600 font-semibold">Semua →</button>
                </div>
                <div className="space-y-2.5">
                  {setoran.length === 0 && (
                    <div className="bg-white rounded-2xl p-6 text-center shadow-sm">
                      <TrendingUp size={28} className="text-emerald-200 mx-auto mb-2" />
                      <p className="text-sm text-gray-400">Belum ada setoran</p>
                    </div>
                  )}
                  {setoran.slice(0, 3).map((s, i) => <SetoranCard key={s.id} s={s} i={i} />)}
                </div>
              </div>

              {/* ═══ FITUR LAINNYA ═══ */}
              <div>
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-3">Fitur Lainnya</p>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
                  {[
                    { icon: BookOpen,    label: 'Al-Quran',      sub: 'Baca Al-Quran digital',    path: '/quran',   color: 'emerald' },
                    { icon: Moon,        label: 'Tasbih Digital', sub: 'Zikir & tasbih harian',   path: '/tasbih',  color: 'amber'   },
                    { icon: Newspaper,   label: 'BQ News',       sub: 'Berita & informasi',       path: '/berita',  color: 'blue'    },
                    { icon: ShoppingBag, label: 'Toko',          sub: 'Produk pesantren',         path: '/toko',    color: 'orange'  },
                    { icon: Heart,       label: 'Donasi',        sub: 'Donasi & infaq',           path: '/donasi',  color: 'red'     },
                  ].map(({ icon: Icon, label, sub, path, color }, idx, arr) => (
                    <button key={label} onClick={() => navigate(path)}
                      className={`bg-white rounded-2xl p-4 shadow-sm border border-gray-100 flex items-center gap-3 hover:bg-gray-50 active:scale-95 transition-all text-left ${arr.length % 2 !== 0 && idx === arr.length - 1 ? 'col-span-2 md:col-span-1' : ''}`}>
                      <div className={`w-10 h-10 rounded-xl bg-${color}-100 flex items-center justify-center flex-shrink-0`}>
                        <Icon size={18} className={`text-${color}-600`} />
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-gray-700 leading-tight">{label}</p>
                        <p className="text-[10px] text-gray-400 mt-0.5">{sub}</p>
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* ═══ CATATAN DARI GURU ═══ */}
              {latestCatatan && (
                <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-6 h-6 rounded-full bg-amber-200 flex items-center justify-center">
                      <CheckCircle size={13} className="text-amber-700" />
                    </div>
                    <p className="text-xs font-bold text-amber-800">Catatan Guru</p>
                    <p className="text-[10px] text-amber-500 ml-auto">{latestCatatan.tanggal}</p>
                  </div>
                  <p className="text-sm text-amber-900 italic leading-relaxed">"{latestCatatan.catatan}"</p>
                  {guruCatatan && (
                    <p className="text-[10px] text-amber-600 mt-1.5 font-medium text-right">— {guruCatatan.name}</p>
                  )}
                </div>
              )}
            </>
          )}

          {/* ── SETORAN VIEW ── */}
          {activeView === 'setoran' && (() => {
            const FILTER_TABS = [
              { key: 'semua', label: 'Semua' },
              { key: 'ziyadah', label: 'Ziyadah' },
              { key: 'bin_nadzar', label: 'Binnadzar' },
              { key: 'murajaah', label: 'Murajaah' },
            ];
            const filteredSetoran = filterJenis === 'semua'
              ? setoran
              : filterJenis === 'bin_nadzar'
                ? setoran.filter(s => ['bin_nadzar', 'bin_nadhar'].includes(s.jenis))
                : setoran.filter(s => s.jenis === filterJenis);
            return (
              <div>
                <div className="flex items-center justify-between mb-3">
                  <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Riwayat Setoran</p>
                  <span className="text-[10px] text-gray-400">{filteredSetoran.length} catatan</span>
                </div>
                {/* Filter Tabs */}
                <div className="flex gap-2 overflow-x-auto pb-2 mb-3 scrollbar-hide">
                  {FILTER_TABS.map(tab => (
                    <button key={tab.key}
                      data-testid={`filter-jenis-${tab.key}`}
                      onClick={() => setFilterJenis(tab.key)}
                      className={`flex-shrink-0 px-3.5 py-1.5 rounded-full text-[11px] font-bold border transition-all duration-200 ${
                        filterJenis === tab.key
                          ? 'bg-emerald-600 text-white border-emerald-600'
                          : 'bg-white text-gray-500 border-gray-200 hover:border-emerald-300'
                      }`}>
                      {tab.label}
                    </button>
                  ))}
                </div>
                <div className="space-y-2.5">
                  {filteredSetoran.length === 0 && (
                    <div className="bg-white rounded-2xl p-8 text-center shadow-sm">
                      <BookCheck size={32} className="text-emerald-200 mx-auto mb-3" />
                      <p className="text-gray-400 text-sm">
                        {filterJenis === 'semua' ? 'Belum ada riwayat setoran' : `Belum ada setoran ${JENIS_LABEL[filterJenis] || filterJenis}`}
                      </p>
                    </div>
                  )}
                  {filteredSetoran.map((s, i) => <SetoranCard key={s.id} s={s} i={i} full />)}
                </div>
              </div>
            );
          })()}

          {/* ── PROGRESS VIEW ── */}
          {activeView === 'progress' && (
            <div className="space-y-4">
              <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100 flex flex-col items-center">
                <CircularProgress percent={progressPct} size={160} />
                <p className="text-sm font-bold text-gray-700 mt-3">Target Hafalan Bulan Ini</p>
                <p className="text-xs text-gray-400">{formatHalaman(halamanBulanIni)} hal · Target: {targetLabel}</p>
              </div>
              <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-bold text-gray-700">Progress Per Juz (Urutan BQ)</h3>
                  {currentJuz && (
                    <span className="text-[11px] bg-amber-100 text-amber-700 font-bold px-2 py-0.5 rounded-full">
                      Sedang Juz {currentJuz}
                    </span>
                  )}
                </div>
                <div className="grid grid-cols-10 gap-1.5">
                  {BQ_ORDER.map((juzNo, bqIdx) => {
                    const isDone    = bqIdx < juzDone;
                    const isCurrent = currentJuz !== null && bqIdx === juzDone;
                    return (
                      <div key={juzNo} data-testid={`progress-juz-${juzNo}`}
                        className={`aspect-square rounded-xl flex items-center justify-center text-[10px] font-bold transition-all ${
                          isDone    ? 'bg-emerald-500 text-white shadow-sm'
                          : isCurrent ? 'bg-amber-400 text-white shadow-sm'
                          : 'bg-gray-100 text-gray-300'
                        }`}>{juzNo}</div>
                    );
                  })}
                </div>
                <div className="flex items-center gap-3 mt-3 text-[10px] text-gray-400">
                  <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-emerald-500 inline-block" /> Selesai</span>
                  <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-amber-400 inline-block" /> Sedang berjalan</span>
                  <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-gray-200 inline-block" /> Belum</span>
                </div>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <StatCard icon={Repeat} value={totalHalamanMurajaah} label="Total Setoran Muroja'ah" color="blue" delay={0.05} />
                <StatCard icon={TrendingUp} value={`${juzDone}/30`} label="Juz Selesai (BQ)" color="amber" delay={0.1} />
                <StatCard icon={BookCheck} value={totalHalamanBinnadzar} label="Total Setoran Binnadzar" color="purple" delay={0.15} />
              </div>
            </div>
          )}

          {/* ── TASMI' VIEW ── */}
          {activeView === 'tasmi' && (
            <div>
              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-3">Riwayat Tasmi'</p>
              <div className="space-y-2.5">
                {setoran.filter(s => s.jenis === 'tasmi').length === 0 && (
                  <div className="bg-white rounded-2xl p-8 text-center shadow-sm">
                    <BarChart2 size={32} className="text-emerald-200 mx-auto mb-3" />
                    <p className="text-gray-400 text-sm">Belum ada catatan Tasmi'</p>
                  </div>
                )}
                {setoran.filter(s => s.jenis === 'tasmi').map((s, i) => (
                  <motion.div key={s.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.04 }}
                    className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
                    <div className="flex items-center justify-between">
                      <div>
                        <span className="text-[10px] font-bold bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">Tasmi'</span>
                        <p className="text-base font-bold text-gray-800 mt-1">{s.jumlah_juz || '—'} Juz</p>
                        <p className="text-xs text-gray-400">{s.tanggal}</p>
                      </div>
                      <span className={`text-sm font-bold px-3 py-1.5 rounded-xl ${
                        s.tasmi_status === 'Lulus' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'
                      }`}>{s.tasmi_status || '—'}</span>
                    </div>
                    {s.catatan && (
                      <p className="text-xs text-amber-700 italic mt-2 bg-amber-50 px-3 py-1.5 rounded-xl">"{s.catatan}"</p>
                    )}
                  </motion.div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}

// ─── Setoran Card Component ─────────────────────────────
function SetoranCard({ s, i, full = false }) {
  const gradeColor = { A: 'bg-blue-100 text-blue-700', B: 'bg-yellow-100 text-yellow-700', C: 'bg-orange-100 text-orange-700', D: 'bg-red-100 text-red-700' };
  const jenisLabel = JENIS_LABEL[s.jenis] || s.jenis;
  const jenisColor = JENIS_COLOR[s.jenis] || 'bg-gray-100 text-gray-600';

  return (
    <motion.div key={s.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
      transition={{ delay: i * 0.04 }}
      className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
      <div className="flex items-start gap-3">
        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full flex-shrink-0 mt-0.5 ${jenisColor}`}>{jenisLabel}</span>
        <div className="flex-1 min-w-0">
          {s.jenis === 'iqro' ? (
            <p className="text-sm font-semibold text-gray-800">Iqro Jilid {s.iqro_jilid} · Hal. {s.iqro_halaman}</p>
          ) : s.jenis === 'tasmi' ? (
            <p className="text-sm font-semibold text-gray-800">{s.jumlah_juz} Juz · <span className={s.tasmi_status === 'Lulus' ? 'text-emerald-600' : 'text-red-600'}>{s.tasmi_status}</span></p>
          ) : (
            <p className="text-sm font-semibold text-gray-800 truncate">
              {describeSetoran(s)}
            </p>
          )}
          <p className="text-[10px] text-gray-400 mt-0.5">
            {s.tanggal}{s.jml_halaman > 0 ? ` · ${formatHalaman(s.jml_halaman)} hal.` : ''}
          </p>
        </div>
        {(s.nilai_huruf || s.grade) ? (
          <div data-testid={`setoran-grade-${s.id}`}
            className={`w-9 h-9 rounded-xl flex items-center justify-center text-sm font-bold flex-shrink-0 ${gradeColor[s.nilai_huruf || s.grade] || 'bg-gray-100 text-gray-600'}`}>
            {s.nilai_huruf || s.grade}
          </div>
        ) : null}
      </div>

      {s.catatan && (
        <p className="text-[11px] text-amber-700 italic mt-2 bg-amber-50 px-3 py-1.5 rounded-xl line-clamp-2">"{s.catatan}"</p>
      )}
    </motion.div>
  );
}
